import { clamp, glow, mulberry32, TAU, type DrawFn } from '../lib/math'

type Pt = { x: number; y: number }

function makeBolt(
  rng: () => number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  gens = 5,
): Pt[] {
  let pts: Pt[] = [
    { x: x1, y: y1 },
    { x: x2, y: y2 },
  ]
  let amp = 70
  for (let g = 0; g < gens; g++) {
    const next: Pt[] = []
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]
      const b = pts[i + 1]
      const mx = (a.x + b.x) / 2 + (rng() - 0.5) * amp
      const my = (a.y + b.y) / 2 + (rng() - 0.5) * amp * 0.55
      next.push(a, { x: mx, y: my })
    }
    next.push(pts[pts.length - 1])
    pts = next
    amp *= 0.48
  }
  return pts
}

export function createVoltage(): DrawFn {
  const rng = mulberry32(21)
  const bolts: { pts: Pt[]; born: number; life: number; thick: number }[] = []
  for (let i = 0; i < 7; i++) {
    bolts.push({
      pts: [],
      born: 0.35 + i * 0.55 + rng() * 0.2,
      life: 0.18 + rng() * 0.16,
      thick: 1.2 + rng() * 2.4,
    })
  }
  const sparks = Array.from({ length: 40 }, () => ({
    x: rng(),
    y: rng(),
    s: 0.6 + rng() * 1.8,
    o: rng() * TAU,
  }))

  let lastW = 0
  let lastH = 0
  const rebuild = (w: number, h: number) => {
    lastW = w
    lastH = h
    const rng2 = mulberry32(21)
    for (const b of bolts) {
      const x1 = w * (0.15 + rng2() * 0.7)
      const y1 = h * (0.02 + rng2() * 0.12)
      const x2 = w * (0.2 + rng2() * 0.6)
      const y2 = h * (0.35 + rng2() * 0.4)
      b.pts = makeBolt(rng2, x1, y1, x2, y2, 6)
    }
  }

  return (ctx, t, w, h) => {
    if (w !== lastW || h !== lastH) rebuild(w, h)
    ctx.clearRect(0, 0, w, h)
    ctx.globalCompositeOperation = 'lighter'

    let flash = 0
    for (const b of bolts) {
      const cycle = 2.6
      const local = (t - b.born + cycle) % cycle
      if (local < 0 || local > b.life) continue
      const fade = 1 - local / b.life
      flash = Math.max(flash, fade)
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.strokeStyle = `rgba(180,230,255,${0.35 * fade})`
      ctx.lineWidth = b.thick * 6
      ctx.beginPath()
      ctx.moveTo(b.pts[0].x, b.pts[0].y)
      for (const p of b.pts) ctx.lineTo(p.x, p.y)
      ctx.stroke()
      ctx.strokeStyle = `rgba(230,250,255,${0.95 * fade})`
      ctx.lineWidth = b.thick
      ctx.stroke()
    }

    if (flash > 0.4) {
      ctx.fillStyle = `rgba(190,220,255,${(flash - 0.4) * 0.18})`
      ctx.fillRect(0, 0, w, h)
    }

    glow(ctx, w * 0.5, h * 0.38, 140, `rgba(80,160,255,${0.08 + flash * 0.18})`)

    for (const s of sparks) {
      const tw = 0.3 + 0.7 * Math.abs(Math.sin(t * 8 + s.o))
      ctx.fillStyle = `rgba(200,240,255,${tw * 0.7 * clamp(t)})`
      ctx.beginPath()
      ctx.arc(s.x * w, s.y * h * 0.7, s.s * tw, 0, TAU)
      ctx.fill()
    }

    ctx.globalCompositeOperation = 'source-over'
  }
}
